import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { catchError, map, switchMap } from 'rxjs';

import { NotificationsService } from '@core/services/notifications.service';
import { isEmptyValue } from '@core/helpers/value-empty-checker.helper';
import { FileReaderHelper } from '@core/helpers/file-reader.helper';
import { UtilityService } from '@core/services/utility.service';
import { ButtonDirective } from '@pages/office/shared/components/button/button.directive';
import { OfficeService } from '@pages/office/services/office.service';
import { commonCatchErrorHandler } from '@pages/office/office.constants';
import { uuid } from '@pages/office/shared/utils/uuid.util';
import { OfficeFormHelperService } from '../../office-form-helper.service';
import { COMPANY_SEQ, FILE_SIZE_ERROR, NOTIFICATION_MESSAGES } from './image-upload.constants';
import { BaseFormControlDirective } from '../base-form-control.directive';
import { ControlWrapperComponent } from '../control-wrapper/control-wrapper.component';
import { ControlHasRequiredPipe } from '../pipes/control-has-required.pipe';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ControlWrapperComponent, ControlHasRequiredPipe, AsyncPipe, ButtonDirective, NgClass],
    selector: 'app-image-upload',
    templateUrl: 'image-upload.component.html',
    styleUrl: 'image-upload.component.scss'
})
export class ImageUploadComponent extends BaseFormControlDirective implements OnInit {
  public imageUrl$ = signal<string>(null);
  public allowedFileTypes = '.bmp,.jpg,.jpeg,.gif,.tif,.tiff';
  public maxFileSize = 5; // MB

  private fileOrigin$ = signal<File>(null);
  private resizedFile: File | null = null;
  private maxSizeMb = this.maxFileSize * 1024 * 1024;
  private companyId: number;
  private officeService = inject(OfficeService);
  private officeFormHelperService = inject(OfficeFormHelperService);
  private notificationsService = inject(NotificationsService);
  private utilityService = inject(UtilityService);
  private commonCatchErrorHandler = commonCatchErrorHandler();

  ngOnInit(): void {
    super.ngOnInit();
    this.companyId = this.officeFormHelperService.currentAnswer$()[COMPANY_SEQ] as number;
    !isEmptyValue(this.control.value) && this.downloadImage();
  }

  public onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (file.size > this.maxSizeMb) {
        this.control.setErrors(FILE_SIZE_ERROR(this.maxFileSize));
        return;
      } else {
        this.control.setErrors(null);
      }
      this.handleFile(file);
      this.fileOrigin$.set(file);
      this.control.setValue(uuid());
      this.control.markAsTouched();
      this.control.markAsDirty();
    }
  }

  public onUpload(): void {
    this.utilityService.openModal();
    FileReaderHelper.readFile(this.fileOrigin$())
      .pipe(
        switchMap(({ name, data }) =>
          this.officeService.uploadHQCompanyLogo({
            companyId: this.companyId,
            image: data,
            originalFileName: name,
            menuImage: this.control.value,
          })
        )
      )
      .subscribe(res => {
        this.utilityService.closeModal();
        if (res.success) {
          this.notificationsService.showSuccess(NOTIFICATION_MESSAGES.saved);
          this.fileOrigin$.set(null);
        } else if (res.error) {
          this.notificationsService.showError(NOTIFICATION_MESSAGES.defaultError);
        }
      });
  }

  public onRemove(): void {
    this.utilityService.openModal();
    this.officeService
      .deleteHQCompanyLogo(this.companyId)
      .pipe(catchError(this.commonCatchErrorHandler))
      .subscribe(res => {
        this.utilityService.closeModal();
        if (res.success) {
          this.notificationsService.showSuccess(NOTIFICATION_MESSAGES.deleted);
          this.control.setValue(null, { onlySelf: true, emitEvent: false });
          this.imageUrl$.set(null);
          this.resizedFile = null;
        }
      });
  }

  private async handleFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) return;
    // Height 54px
    // Width 120px
    this.resizedFile = await FileReaderHelper.imageResizeUtil(file, 120, 54);
    this.imageUrl$.set(URL.createObjectURL(this.resizedFile));
  }

  private downloadImage(): void {
    this.officeService
      .getHQCompanyLogo(this.companyId)
      .pipe(
        map(blob => new File([blob], null, { type: blob.type })),
        catchError(this.commonCatchErrorHandler)
      )
      .subscribe(file => {
        if (!file.size || !file.type.startsWith('image/')) {
          this.control.reset();
          return;
        }
        this.handleFile(file);
      });
  }
}
